import React from "react";
import { CalendarDays, ClipboardList, Clock, Coins, Crown, Download, Gauge, Package, Receipt, UserRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import { exportarExcel } from "@/features/relatorio-vendas/lib/excel";
import { formatCurrency, formatInt } from "@/features/relatorio-vendas/lib/formato";
import { rotuloArquivo } from "@/features/relatorio-vendas/lib/periodo";

import { Destaques, Kpi, Nota, Secao } from "../componentes/Base";
import Faixas from "../componentes/Faixas";
import GraficoMetrica from "../componentes/GraficoMetrica";
import Tabela from "../componentes/Tabela";
import { comparador, formatarDecimal, formatarMinutos, reaisEixo } from "../regras";

const porHora = (v) => (v == null ? "—" : `${formatCurrency(v)}/h`);
const minutosEixo = (v) => `${Math.round(v)} min`;
const nomeComanda = (n) => `Comanda ${String(n).padStart(2, "0")}`;

const COLUNAS_COMUNS = (rotuloVendas) => [
  { key: "faturamento", rotulo: "Faturou", formatar: formatCurrency, barra: true },
  { key: "vendas", rotulo: rotuloVendas, formatar: formatInt },
  { key: "ticket_medio", rotulo: "Ticket", formatar: formatCurrency },
  { key: "tempo_medio", rotulo: "Tempo", formatar: formatarMinutos, dica: "Tempo médio entre abrir e fechar" },
  { key: "itens_media", rotulo: "Itens", formatar: formatarDecimal },
  { key: "faturamento_hora", rotulo: "R$ por hora", formatar: porHora },
];

const EXCEL = [
  { header: "Comanda", value: (c) => nomeComanda(c.numero) },
  { header: "Faturamento (R$)", value: (c) => c.faturamento },
  { header: "Vezes aberta", value: (c) => c.vendas },
  { header: "Ticket médio (R$)", value: (c) => c.ticket_medio },
  { header: "Tempo médio (min)", value: (c) => c.tempo_medio ?? "" },
  { header: "Itens por comanda", value: (c) => c.itens_media ?? "" },
  { header: "R$ por hora", value: (c) => c.faturamento_hora ?? "" },
];

function destaques(d) {
  const k = d.kpis;
  const topo = d.ranking[0];
  const atendente = d.atendentes.find((a) => a.nome !== "Sem registro");
  const pico = [...d.por_hora].sort((a, b) => b.faturamento - a.faturamento)[0];
  return [
    topo && { icone: Crown, texto: <><b>{nomeComanda(topo.numero)}</b> é a que mais faturou: {formatCurrency(topo.faturamento)}.</> },
    atendente && d.atendentes.length > 1 && { icone: UserRound,
      texto: <><b>{atendente.nome}</b> abriu as comandas que mais faturaram: {formatCurrency(atendente.faturamento)}.</> },
    pico && { icone: Clock, texto: <>As comandas abertas às <b>{pico.rotulo}</b> são as que mais faturam: {formatCurrency(pico.faturamento)} no período.</> },
    k.abertas_agora > 0 && { icone: ClipboardList, cor: "bg-muted text-muted-foreground",
      texto: <><b>{k.abertas_agora}</b> {k.abertas_agora === 1 ? "comanda ainda aberta" : "comandas ainda abertas"} (fora do tempo médio).</> },
  ];
}

// Relatório de Tempo → Comandas: quanto tempo a comanda fica aberta e quanto fatura.
export default function ComandasAba({ dados, periodo }) {
  const k = dados.kpis;
  if (!k.vendas) return <Nota>Nenhuma comanda foi aberta nesse período.</Nota>;
  const cmp = comparador(k, dados.anterior?.kpis);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-e2e="kpis-comandas">
        <Kpi destaque rotulo="Tempo de comanda aberta" icone={Clock} valor={formatarMinutos(k.tempo_medio)}
          {...cmp("tempo_medio", formatarMinutos, { tipo: "min", melhor: null })}
          detalhe={k.permanencia_mediana != null ? `Metade fecha em até ${formatarMinutos(k.permanencia_mediana)}` : null} />
        <Kpi rotulo="Faturamento" icone={Coins} valor={formatCurrency(k.faturamento)} detalhe={`${formatInt(k.vendas)} comandas`}
          {...cmp("faturamento", formatCurrency)} />
        <Kpi rotulo="Ticket médio" icone={Receipt} valor={formatCurrency(k.ticket_medio)} {...cmp("ticket_medio", formatCurrency)} />
        <Kpi rotulo="Itens por comanda" icone={Package} valor={formatarDecimal(k.itens_media)} {...cmp("itens_media", formatarDecimal)}
          detalhe={k.faturamento_hora != null ? `${porHora(k.faturamento_hora)} enquanto aberta` : null} />
      </div>

      <Destaques itens={destaques(dados)} />

      <Secao titulo="Comanda a comanda" icone={ClipboardList} e2e="ranking-comandas"
        explicacao="Pelo número do cartão. Toque no título da coluna para ordenar."
        direita={(
          <Button variant="outline" size="sm" className="gap-1.5 h-8"
            onClick={() => exportarExcel(dados.ranking, EXCEL, `Tempo_Comandas_${rotuloArquivo(periodo)}.xlsx`)}>
            <Download className="w-4 h-4" /> Excel
          </Button>
        )}>
        <Tabela linhas={dados.ranking} ordemInicial={{ key: "faturamento", dir: "desc" }}
          colunas={[{ key: "numero", rotulo: "Comanda", render: (c) => <span className="font-semibold">{nomeComanda(c.numero)}</span> }, ...COLUNAS_COMUNS("Vezes aberta")]} />
      </Secao>

      {dados.atendentes.length > 0 && (
        <Secao titulo="Por quem abriu" icone={UserRound} e2e="atendentes-comandas" explicacao="Quem abriu a comanda no painel ou no app do garçom.">
          <Tabela linhas={dados.atendentes} ordemInicial={{ key: "faturamento", dir: "desc" }}
            colunas={[{ key: "nome", rotulo: "Atendente", render: (a) => <span className="font-semibold">{a.nome}</span> }, ...COLUNAS_COMUNS("Comandas")]} />
        </Secao>
      )}

      <GraficoMetrica titulo="Hora a hora" icone={Gauge} e2e="horas-comandas" dados={dados.por_hora}
        explicacao="Pela hora em que a comanda foi aberta."
        metricas={[
          { key: "faturamento", rotulo: "Faturamento", formatar: formatCurrency, eixo: reaisEixo },
          { key: "abertas", rotulo: "Abertas", formatar: formatInt },
          { key: "tempo_medio", rotulo: "Tempo aberta", formatar: formatarMinutos, eixo: minutosEixo },
        ]} />

      <Faixas faixas={dados.faixas} titulo="Quanto tempo a comanda fica aberta"
        explicacao="Quantas comandas caem em cada faixa de tempo e o ticket de cada faixa." />

      <GraficoMetrica titulo="Dia da semana" icone={CalendarDays} e2e="dias-comandas" dados={dados.por_dia_semana}
        metricas={[
          { key: "faturamento", rotulo: "Faturamento", formatar: formatCurrency, eixo: reaisEixo },
          { key: "vendas", rotulo: "Comandas", formatar: formatInt },
          { key: "tempo_medio", rotulo: "Tempo aberta", formatar: formatarMinutos, eixo: minutosEixo },
        ]} />

      <div className="space-y-1">
        <Nota>Comanda cancelada não entra. Comanda juntada em outra entra na que recebeu.</Nota>
        {k.sem_tempo > 0 && <Nota>{formatInt(k.sem_tempo)} {k.sem_tempo === 1 ? "comanda ficou" : "comandas ficaram"} fora do tempo médio (mais de 16h aberta).</Nota>}
      </div>
    </>
  );
}
